import React, { useState, useRef, useMemo, useCallback, useContext } from 'react';
import { View, Text, Platform, ScrollView, Pressable, Alert, TextInput, useWindowDimensions, Modal, ActivityIndicator } from 'react-native';
import { WebView } from 'react-native-webview';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRoute, useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import { Button, Input, TextArea, XStack, YStack, Separator, Sheet, Label, Switch, SizableText, Card } from 'tamagui';
import { AntDesign, MaterialIcons, Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import DefaultScreen from '@components/Global/DefaultScreenView';
import apis from '@api/api';
import styles from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { DefaultRootState } from '@reduxLocal/persistState';
import ErrorBoundary from '@utils/ErrorBoundary';
import { useEditorContext } from '@hooks/toolbarContext';
import { MotiView } from 'moti';
import DomEditor, { DomEditorHandle } from '../../../../LowLevel/Components/DomEditor';
import DocumentDomEditor, { DocumentDomEditorHandle } from './Components/DocumentDomEditor';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import { createDocumentTemplate, updateDocumentTemplate } from '@api/api';

interface DocumentData {
  _id?: string;
  content: string;
  title: string;
  description?: string;
  data?: Record<string, any>;
  checkBoxes?: Array<{
    label: string;
    checked: boolean;
    required: boolean;
    value: string;
  }>;
  isTemplate?: boolean;
  global?: boolean;
}

interface HandlebarsField {
  key: string;
  label: string;
  value: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'checkbox';
}

const DocumentTemplateEditor = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const theme = themeSelector();
  const queryClient = useQueryClient();
  const webViewRef = useRef<WebView>(null);
  const { width } = useWindowDimensions();
  
  const { templateId } = route.params as { templateId?: string };
  const isEditing = !!templateId;
  
  const activeCompany = useSelector((state: DefaultRootState) => state.promiseData.data.companyData);
  
  const currentUser = useSelector((state: DefaultRootState) => state.promiseData.data);

  // State for document data
  const [documentData, setDocumentData] = useState<DocumentData>({
    content: '',
    title: '',
    description: '',
    data: {},
    checkBoxes: [],
    isTemplate: true,
    global: false,
  });

  // State for template fields editor
  const [showFieldsEditor, setShowFieldsEditor] = useState(false);
  const [templateFields, setTemplateFields] = useState<HandlebarsField[]>([]);
  const [isAutoDetecting, setIsAutoDetecting] = useState(false);
  
  // State for edit mode and Quill editor - default to rich edit mode
  const [isEditMode, setIsEditMode] = useState(true);
  const quillEditorRef = useRef<DocumentDomEditorHandle>(null);
  const { setEditorContext, isEditorConnected } = useEditorContext();
  const { showConfirm } = useAlertDialog();

  // State for left panel width toggle
  const [leftPanelWide, setLeftPanelWide] = useState(false);

  // State for document upload
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Reset initialization when templateId changes
  React.useEffect(() => {
    setHasInitialized(false);
  }, [templateId]);

  // Cleanup timers on unmount
  React.useEffect(() => {
    return () => {
      if (window.fieldDetectionTimer) {
        clearTimeout(window.fieldDetectionTimer);
      }
      if (window.stateUpdateTimer) {
        clearTimeout(window.stateUpdateTimer);
      }
    };
  }, []);

  // Fetch document template if editing
  const { data: templateData, isLoading } = useQuery({
    queryKey: ['documentTemplate', templateId],
    queryFn: async () => {
      if (!templateId) return null;
      const response = await apis.getDocumentTemplates();
      return response.find((template: any) => template._id === templateId);
    },
    enabled: isEditing,
  });

  // Extract handlebars fields from content - optimized to avoid frequent re-runs
  const extractHandlebarsFields = useCallback((content: string): HandlebarsField[] => {
    // First, clean up the content to extract plain handlebars expressions
    let cleanContent = content;
    
    // Remove HTML tags that might wrap handlebars expressions
    cleanContent = cleanContent.replace(/<[^>]*>(\{\{\{[^}]+\}\}\})<\/[^>]*>/g, '$1');
    cleanContent = cleanContent.replace(/<[^>]*(\{\{\{[^}]+\}\}\})[^>]*>/g, '$1');
    
    // Extract text content from HTML to find handlebars expressions
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    const regex = /\{\{\{([^}]+)\}\}\}/g;
    const fields: HandlebarsField[] = [];
    const found = new Set<string>();
    
    let match;
    while ((match = regex.exec(textContent)) !== null) {
      const key = match[1].trim();
      if (!found.has(key)) {
        found.add(key);
        fields.push({
          key,
          label: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
          value: '', // Don't use documentData.data here to avoid frequent re-runs
          type: 'text'
        });
      }
    }
    
    return fields;
  }, []); // Remove documentData.data dependency

  // Helper function to merge new fields with existing ones while preserving values
  const mergeTemplateFields = useCallback((newFields: HandlebarsField[]) => {
    if (newFields.length === 0) return;

    setTemplateFields(prev => {
      const existingFieldMap = new Map(prev.map(field => [field.key, field]));
      const mergedFields = [...prev];
      let hasNewFields = false;
      
      newFields.forEach(newField => {
        if (!existingFieldMap.has(newField.key)) {
          // Add new field, but preserve any existing value from documentData
          mergedFields.push({
            ...newField,
            value: documentData.data?.[newField.key] || newField.value
          });
          hasNewFields = true;
        }
      });
      
      // Only show auto-detecting indicator and update state if we actually added new fields
      if (hasNewFields) {
        setIsAutoDetecting(true);
        // Clear auto-detecting state after a short delay
        setTimeout(() => setIsAutoDetecting(false), 1000);
        return mergedFields;
      }

      return prev; // Return previous state if no changes
    });
  }, [documentData.data]);

  // Load template data when fetched and initialize template fields
  const [hasInitialized, setHasInitialized] = useState(false);

  React.useEffect(() => {
    if (templateData && !hasInitialized) {
      setDocumentData({
        ...templateData,
        data: templateData.data || {},
      });

      // Initialize template fields from loaded content
      if (templateData.content) {
        const fields = extractHandlebarsFields(templateData.content);
        setTemplateFields(fields);
      }

      setHasInitialized(true);
    }
  }, [templateData, extractHandlebarsFields, hasInitialized]);

  // Initialize data from template or empty state
  const initializeData = useCallback((fromTemplate = true) => {
    if (fromTemplate && templateData) {
      setDocumentData({
        ...templateData,
        data: templateData.data || {},
      });

      // Initialize template fields from loaded content
      if (templateData.content) {
        const fields = extractHandlebarsFields(templateData.content);
        setTemplateFields(fields);
      }
    } else {
      // Reset to empty state
      setDocumentData({
        content: '',
        title: '',
        description: '',
        data: {},
        checkBoxes: [],
        isTemplate: true,
        global: false,
      });
      setTemplateFields([]);
    }
    setHasInitialized(true);
  }, [templateData, extractHandlebarsFields]);

  // Revert function with confirmation dialog
  const handleRevert = async () => {
    const res = await showConfirm(
      'Are you sure you want to revert all changes?',
      'This will restore the document to its original state and cannot be undone.',
      {
        confirmText: 'Revert',
        cancelText: 'Cancel'
      }
    );

    if (res) {
      if (isEditing && templateData) {
        // Reinitialize with template data
        initializeData(true);
      } else {
        // Reset to empty state for new documents
        initializeData(false);
      }

      // Reset edit mode to default
      setIsEditMode(true);
      setShowFieldsEditor(false);

      Alert.alert('Success', 'Document has been reverted to its original state.');
    }
  }

  // Parse DOCX file using mammoth
  const parseDocxFile = async (uri: string): Promise<string> => {
    try {
      if (Platform.OS === 'web') {
        // For web platform
        const mammoth = await import('mammoth');
        const response = await fetch(uri);
        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        return result.value;
      } else {
        // For native platforms
        const base64 = await FileSystem.readAsStringAsync(uri, {
          encoding: 'base64' as any,
        });
        const mammoth = await import('mammoth');
        const buffer = Buffer.from(base64, 'base64');
        const result = await mammoth.convertToHtml({ buffer });
        return result.value;
      }
    } catch (error) {
      console.error('Error parsing DOCX:', error);
      throw new Error('Failed to parse DOCX file. Please try again.');
    }
  };

  // Parse PDF file using pdfjs-dist
  const parsePdfFile = async (uri: string): Promise<string> => {
    try {
      if (Platform.OS === 'web') {
        // For web platform
        const pdfjsLib = await import('pdfjs-dist');
        // Set worker source
        pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

        const response = await fetch(uri);
        const arrayBuffer = await response.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        let fullText = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map((item: any) => item.str).join(' ');
          fullText += pageText + '\n\n';
        }

        // Convert plain text to HTML with basic formatting
        return fullText
          .split('\n\n')
          .filter(p => p.trim())
          .map(p => `<p>${p.trim()}</p>`)
          .join('\n');
      } else {
        // For native platforms, we need to read the file differently
        Alert.alert('Notice', 'PDF parsing on mobile is limited. For best results, use the web version.');
        const base64 = await FileSystem.readAsStringAsync(uri, {
          encoding: 'base64' as any,
        });
        // Basic text extraction (limited functionality on native)
        return '<p>PDF content could not be fully extracted on mobile. Please use web version for better results.</p>';
      }
    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw new Error('Failed to parse PDF file. Please try again.');
    }
  };

  // Handle document upload
  const handleDocumentUpload = async () => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Pick document
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        setIsUploading(false);
        return;
      }

      const file = result.assets[0];
      setUploadProgress(30);

      let parsedContent = '';

      // Determine file type and parse accordingly
      if (file.mimeType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        parsedContent = await parsePdfFile(file.uri);
      } else if (
        file.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.name.toLowerCase().endsWith('.docx')
      ) {
        parsedContent = await parseDocxFile(file.uri);
      } else {
        throw new Error('Unsupported file type. Please upload a PDF or DOCX file.');
      }

      setUploadProgress(70);

      // Insert parsed content into editor
      if (quillEditorRef.current && parsedContent) {
        const currentContent = quillEditorRef.current.getContent() || '';
        const separator = currentContent.trim() ? '\n\n' : '';
        const newContent = currentContent + separator + parsedContent;
        quillEditorRef.current.setContent(newContent);
        setDocumentData(prev => ({ ...prev, content: newContent }));

        // Auto-detect template fields from the uploaded content
        setTimeout(() => {
          const detectedFields = extractHandlebarsFields(newContent);
          if (detectedFields.length > 0) {
            mergeTemplateFields(detectedFields);
          }
        }, 500);
      }

      setUploadProgress(100);
      Alert.alert('Success', `Document "${file.name}" has been uploaded and parsed successfully!`);

    } catch (error: any) {
      console.error('Document upload error:', error);
      Alert.alert('Upload Error', error.message || 'Failed to upload and parse document');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Memoize editor context functions to prevent recreation on every render
  const editorContextFunctions = useMemo(() => ({
    insertContent: (content: string) => {
      if (quillEditorRef.current) {
        try {
          const currentContent = quillEditorRef.current.getContent() || '';
          const separator = currentContent.trim() ? '\n\n' : '';
          const newContent = currentContent + separator + content;
          quillEditorRef.current.setContent(newContent);
          // Update document data as well
          setDocumentData(prev => ({ ...prev, content: newContent }));
          console.log('Content inserted into document template editor successfully');
        } catch (error) {
          console.error('Failed to insert content into document template editor:', error);
        }
      }
    },
    setContent: (content: string) => {
      if (quillEditorRef.current) {
        try {
          quillEditorRef.current.setContent(content);
          setDocumentData(prev => ({ ...prev, content }));
          console.log('Content set in document template editor successfully');
        } catch (error) {
          console.error('Failed to set content in document template editor:', error);
        }
      }
    },
    getContent: () => {
      try {
        return quillEditorRef.current?.getContent() || '';
      } catch (error) {
        console.error('Failed to get content from document template editor:', error);
        return '';
      }
    },
    appendContent: (content: string) => {
      if (quillEditorRef.current) {
        try {
          const currentContent = quillEditorRef.current.getContent() || '';
          const newContent = currentContent + content;
          quillEditorRef.current.setContent(newContent);
          setDocumentData(prev => ({ ...prev, content: newContent }));
          console.log('Content appended to document template editor successfully');
        } catch (error) {
          console.error('Failed to append content to document template editor:', error);
        }
      }
    },
    prependContent: (content: string) => {
      if (quillEditorRef.current) {
        try {
          const currentContent = quillEditorRef.current.getContent() || '';
          const newContent = content + currentContent;
          quillEditorRef.current.setContent(newContent);
          setDocumentData(prev => ({ ...prev, content: newContent }));
          console.log('Content prepended to document template editor successfully');
        } catch (error) {
          console.error('Failed to prepend content to document template editor:', error);
        }
      }
    },
    clearContent: () => {
      if (quillEditorRef.current) {
        try {
          quillEditorRef.current.setContent('');
          setDocumentData(prev => ({ ...prev, content: '' }));
          console.log('Document template editor content cleared successfully');
        } catch (error) {
          console.error('Failed to clear document template editor content:', error);
        }
      }
    }
  }), []); // Empty dependencies since these functions should be stable

  // Memoize the complete editor context object
  const documentEditorContext = useMemo(() => ({
    ref: quillEditorRef,
    isConnected: true,
    contentWidth: width,
    contentHeight: 400,
    ...editorContextFunctions
  }), [width, editorContextFunctions]);

  // Register editor in context when in edit mode and available
  React.useEffect(() => {
    if (isEditMode && setEditorContext) {
  // Register the memoized editor context
      setEditorContext(documentEditorContext);
      console.log('Document template editor context registered with HelpSidePanel');
    } else if (!isEditMode && setEditorContext) {
      // Clear editor context when not in edit mode
      setEditorContext(null);
      console.log('Document template editor context cleared from HelpSidePanel');
    }

    return () => {
      // Clear editor context when component unmounts or edit mode changes
      if (setEditorContext) {
        setEditorContext(null);
        console.log('Document template editor context cleanup completed');
      }
    };
  }, [isEditMode, setEditorContext, documentEditorContext]);

  // Replace handlebars in content with actual values - optimized for performance
  const processedContent = useMemo(() => {
    let content = documentData.content;
    
    // Clean up any Quill formatting that might interfere with handlebars
    // Remove any HTML tags around handlebars expressions
    content = content.replace(/<[^>]*>(\{\{\{[^}]+\}\}\})<\/[^>]*>/g, '$1');
    content = content.replace(/<[^>]*(\{\{\{[^}]+\}\}\})[^>]*>/g, '$1');
    
    // Also handle cases where the handlebars might be split across HTML elements
    content = content.replace(/<[^>]*>\{\{\{/g, '{{{');
    content = content.replace(/\}\}\}<\/[^>]*>/g, '}}}');
    
    // Use existing templateFields for replacement - don't auto-detect here during editing
    templateFields.forEach(field => {
      const regex = new RegExp(`\\{\\{\\{${field.key}\\}\\}\\}`, 'g');
      content = content.replace(regex, field.value || `{{{${field.key}}}}`);
    });
    
    return content;
  }, [documentData.content, templateFields]);

  // Generate HTML with styling for preview
  const htmlContent = useMemo(() => {
    if (!processedContent) return '';
    
    return `
      <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
          <style>
            @page {
              size: A4;
              margin: 0.5in;
            }
            
            @media print {
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 0;
                width: 210mm;
                min-height: 297mm;
                outline: none;
              }
            }
            
            body {
              font-family: Arial, sans-serif;
              width: 100%;
              max-width: 100%;
              min-height: 100vh;
              margin: 0;
              padding: 15mm;
              box-sizing: border-box;
              line-height: 1.6;
              background-color: #ffffff;
            }
            
            .document-container {
              background: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
              width: 100%;
              max-width: 100%;
              box-sizing: border-box;
            }
            
            .document-header {
              border-bottom: 2px solid #e5e7eb;
              padding-bottom: 20px;
              margin-bottom: 30px;
              width: 100%;
            }
            
            .document-title {
              font-size: 24px;
              font-weight: bold;
              color: #1f2937;
              margin-bottom: 10px;
              word-wrap: break-word;
            }
            
            .document-content {
              font-size: 14px;
              color: #374151;
              width: 100%;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
            
            .handlebars-field {
              background-color: #f3f4f6;
              padding: 4px 8px;
              border-radius: 4px;
              border: 1px solid #d1d5db;
              display: inline-block;
              margin: 2px;
            }
            
            h1, h2, H5, h4, h5, h6 {
              color: #1f2937;
              margin-top: 20px;
              margin-bottom: 10px;
              word-wrap: break-word;
            }
            
            p {
              margin-bottom: 15px;
              word-wrap: break-word;
            }
            
            .checkbox-container {
              margin: 10px 0;
              padding: 10px;
              border: 1px solid #e5e7eb;
              border-radius: 4px;
              width: 100%;
              box-sizing: border-box;
            }
            
            @media (max-width: 768px) {
              body {
                padding: 10mm;
              }
              
              .document-container {
                padding: 15px;
              }
            }
          </style>
        </head>
        <body>
          <div class="document-container">
            <div class="document-header">
              <div class="document-title">${documentData.title || 'Untitled Document'}</div>
              ${documentData.description ? `<p style="color: #6b7280; font-style: italic;">${documentData.description}</p>` : ''}
            </div>
            <div class="document-content">
              ${processedContent}
            </div>
            ${documentData.checkBoxes?.length ? `
              <div style="margin-top: 30px; border-top: 1px solid #e5e7eb; padding-top: 20px;">
                <h4>Required Actions:</h4>
                ${documentData.checkBoxes.map(checkbox => `
                  <div class="checkbox-container">
                    <input type="checkbox" ${checkbox.checked ? 'checked' : ''} ${checkbox.required ? 'required' : ''}> 
                    <label style="margin-left: 8px;">${checkbox.label}</label>
                    ${checkbox.value ? `<p style="margin-left: 20px; color: #6b7280; font-size: 12px;">${checkbox.value}</p>` : ''}
                  </div>
                `).join('')}
              </div>
            ` : ''}
          </div>
        </body>
      </html>
    `;
  }, [processedContent, documentData]);

  // Save/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: DocumentData) => {
      const payload = {
        ...data,
        data: templateFields.reduce((acc, field) => {
          acc[field.key] = field.value;
          return acc;
        }, {} as Record<string, any>),
        createdBy: currentUser?._id,
        companyId: activeCompany?._id,
      };
      
      // For now, we'll use a placeholder API call since the specific methods don't exist yet
      // In a real implementation, you would add these to your API
      if (isEditing) {
        await updateDocumentTemplate(templateId, payload);
      } else {
        await createDocumentTemplate(payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentTemplates'] });
      Alert.alert('Success', 'Document template saved successfully!');
      // navigation.goBack();
    },
    onError: (error) => {
      Alert.alert('Error', 'Failed to save document template');
      console.error('Save error:', error);
    },
  });

  // Update field value
  const updateFieldValue = (key: string, value: string) => {
    setTemplateFields(prev => prev.map(field => 
      field.key === key ? { ...field, value } : field
    ));
  };

  // Handle Quill editor content change with debouncing to prevent focus issues
  const handleQuillContentChange = useCallback(async (content: string) => {
    // Don't update state immediately to avoid focus jumping
    // Instead, store the content temporarily and debounce the state update

    // Debounced state update to avoid frequent re-renders
    const updateState = () => {
      setDocumentData(prev => ({ ...prev, content }));
    };

    // Debounced field detection to avoid interfering with editing
    const detectFields = () => {
      const detectedFields = extractHandlebarsFields(content);
      const existingFieldKeys = new Set(templateFields.map(field => field.key));
      const newFields = detectedFields.filter(field => !existingFieldKeys.has(field.key));

      if (newFields.length > 0) {
        mergeTemplateFields(newFields);
      }
    };

    // Clear existing timers
    if (window.stateUpdateTimer) {
      clearTimeout(window.stateUpdateTimer);
    }
    if (window.fieldDetectionTimer) {
      clearTimeout(window.fieldDetectionTimer);
    }

    // Debounce state update (shorter delay for responsiveness)
    window.stateUpdateTimer = setTimeout(updateState, 500); // Update state after 500ms of no typing

    // Debounce field detection (longer delay to avoid disrupting editing flow)
    window.fieldDetectionTimer = setTimeout(detectFields, 2000); // Wait 2 seconds after user stops typing
  }, [extractHandlebarsFields, templateFields, mergeTemplateFields]);

  // Handle template field insertion from Quill editor
  const handleTemplateFieldInserted = useCallback((fieldName: string) => {
    // Check if field already exists in templateFields
    const existingField = templateFields.find(field => field.key === fieldName);
    if (!existingField) {
      // Add new field to templateFields
      const newField: HandlebarsField = {
        key: fieldName,
        label: fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
        value: '',
        type: 'text'
      };
      setTemplateFields(prev => [...prev, newField]);
    }
  }, [templateFields]);

  // Add new checkbox
  const addCheckbox = () => {
    setDocumentData(prev => ({
      ...prev,
      checkBoxes: [
        ...(prev.checkBoxes || []),
        { label: '', checked: false, required: false, value: '' }
      ]
    }));
  };

  // Update checkbox
  const updateCheckbox = (index: number, field: string, value: any) => {
    setDocumentData(prev => ({
      ...prev,
      checkBoxes: prev.checkBoxes?.map((checkbox, i) => 
        i === index ? { ...checkbox, [field]: value } : checkbox
      ) || []
    }));
  };

  // Remove checkbox
  const removeCheckbox = (index: number) => {
    setDocumentData(prev => ({
      ...prev,
      checkBoxes: prev.checkBoxes?.filter((_, i) => i !== index) || []
    }));
  };

  if (isLoading) {
    return (
      <DefaultScreen>
        <View style={[styles.centerEverything, { flex: 1 }]}>
          <Text>Loading template...</Text>
        </View>
      </DefaultScreen>
    );
  }

  return (
    <ErrorBoundary>
      <DefaultScreen
        backButton
        searchBar={{
          customIcons: [
            {
              function: handleDocumentUpload,
              functionName: "uploadDocument",
              icon: <MaterialIcons name="upload-file" size={16} color="white" />,
              disabled: isUploading
            },
            {
              function: handleRevert,
              functionName: "revert",
              icon: <Ionicons name="refresh" size={16} color="white" />
            },
            {
              function: () => setShowFieldsEditor(true),
              functionName: 'editFields',
              icon: <MaterialIcons name="edit" size={16} color="white" />
            },
            {
              function: () => saveMutation.mutate(documentData),
              functionName: 'save',
              icon: <AntDesign name="save" size={16} color="white" />
            }
          ]
        }}
      >
        <XStack flexWrap='wrap' style={{ flex: 1, alignSelf: 'stretch', flexDirection: 'row', gap: 16, padding: 16, height: '100%' }}>
          {/* Left Panel - Editor */}
          <View style={{ flex: 1, maxWidth: leftPanelWide ? '100%' : '30%', minHeight: 0, height: '100%' }}>
            <ScrollView
              style={{ flex: 1, height: '100%' }}
              nestedScrollEnabled={true}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ paddingBottom: 20, minHeight: '100%' }}
              bounces={true}
            >
              {/* Document Info Section */}
              <Card
                backgroundColor={theme ? "#f8fafc" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$4"
                marginBottom="$4"
                elevate
              >
                <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
                  <XStack alignItems="center" gap="$2">
                    <View style={{
                      backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
                      borderRadius: 12,
                      padding: 10
                    }}>
                      <MaterialIcons
                        name="description"
                        size={20}
                        color={theme ? "#0ea5e9" : "#38bdf8"}
                      />
                    </View>
                    <Text style={{ fontSize: 16, fontWeight: '600', color: theme ? '#1e293b' : '#f1f5f9' }}>
                      Document Information
                    </Text>
                  </XStack>
                  <Button
                    size="$3"
                    backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                    color="white"
                    borderRadius={12}
                    onPress={() => setLeftPanelWide(prev => !prev)}
                  >
                    <XStack alignItems="center" gap="$1">
                      <MaterialIcons name={leftPanelWide ? "view-compact" : "view-agenda"} size={14} color="white" />
                      <SizableText color="white" size="$2" fontWeight="600">
                        {leftPanelWide ? 'Narrow' : 'Wide'}
                      </SizableText>
                    </XStack>
                  </Button>
                </XStack>

                <YStack gap="$3">
                  <Input
                    placeholder="Document Title"
                    value={documentData.title}
                    onChangeText={(text) => setDocumentData(prev => ({ ...prev, title: text }))}
                    backgroundColor={theme ? "#ffffff" : "#0f172a"}
                    borderColor={theme ? "#cbd5e1" : "#475569"}
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                    fontSize={14}
                  />

                  <TextArea
                    placeholder="Document Description (optional)"
                    value={documentData.description}
                    onChangeText={(text) => setDocumentData(prev => ({ ...prev, description: text }))}
                    backgroundColor={theme ? "#ffffff" : "#0f172a"}
                    borderColor={theme ? "#cbd5e1" : "#475569"}
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                    numberOfLines={2}
                    fontSize={14}
                  />
                </YStack>

                {/* Upload Progress Indicator */}
                {isUploading && (
                  <Card
                    backgroundColor={theme ? "#dbeafe" : "#0c4a6e"}
                    borderColor={theme ? "#93c5fd" : "#075985"}
                    borderRadius={12}
                    padding="$3"
                    marginTop="$3"
                    marginBottom="$3"
                  >
                    <XStack alignItems="center" gap="$3">
                      <ActivityIndicator size="small" color={theme ? "#0ea5e9" : "#38bdf8"} />
                      <YStack flex={1}>
                        <SizableText size="$3" color={theme ? "#0c4a6e" : "#bae6fd"} fontWeight="600">
                          Uploading and parsing document...
                        </SizableText>
                        <SizableText size="$2" color={theme ? "#475569" : "#7dd3fc"}>
                          {uploadProgress}% complete
                        </SizableText>
                      </YStack>
                    </XStack>
                  </Card>
                )}

                {/* Content Editor with Edit Mode Toggle */}
                <View style={{ minHeight: 800, marginTop: 16 }}>
                  <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons
                        name="edit-note"
                        size={18}
                        color={theme ? "#0ea5e9" : "#38bdf8"}
                      />
                      <Text style={{ fontSize: 14, fontWeight: '600', color: theme ? '#1e293b' : '#f1f5f9' }}>
                        Document Content
                      </Text>
                    </XStack>
                    <Button
                      size="$2"
                      backgroundColor={isEditMode ? (theme ? "#059669" : "#10b981") : (theme ? "#e2e8f0" : "#475569")}
                      color={isEditMode ? "white" : (theme ? "#64748b" : "#cbd5e1")}
                      borderRadius={12}
                      onPress={() => setIsEditMode(!isEditMode)}
                    >
                      <XStack alignItems="center" gap="$1">
                        <MaterialIcons
                          name={isEditMode ? "edit" : "text-fields"}
                          size={14}
                          color={isEditMode ? "white" : (theme ? "#64748b" : "#cbd5e1")}
                        />
                        <SizableText
                          color={isEditMode ? "white" : (theme ? "#64748b" : "#cbd5e1")}
                          size="$2"
                          fontWeight="600"
                        >
                          {isEditMode ? 'Rich Editor' : 'Simple'}
                        </SizableText>
                      </XStack>
                    </Button>
                  </XStack>

                  <Card
                    backgroundColor={theme ? "#ffffff" : "#0f172a"}
                    borderColor={theme ? "#cbd5e1" : "#475569"}
                    borderRadius={12}
                    padding={0}
                    margin={-20}
                    marginBottom={0}
                  >
                    <MotiView
                      animate={{
                        height: isEditMode ? 400 : 120,
                      }}
                      transition={{
                        type: 'timing',
                        duration: 300,
                      }}
                      style={{
                        overflow: 'visible',
                      }}
                    >
                      {isEditMode ? (
                        // Quill Rich Text Editor
                        Platform.OS === 'web' ? (
                          <DocumentDomEditor
                            ref={quillEditorRef}
                            initialContent={documentData.content}
                            saveFn={handleQuillContentChange}
                            onTemplateFieldInserted={handleTemplateFieldInserted}
                            hideBack={true}
                          />
                        ) : (
                          // Fallback for non-web platforms
                          <TextArea
                            placeholder="Document Content (use {{{fieldName}}} for template fields)"
                            value={documentData.content}
                            onChangeText={(text) => setDocumentData(prev => ({ ...prev, content: text }))}
                            backgroundColor={theme ? "#ffffff" : "#0f172a"}
                            borderColor={theme ? "#cbd5e1" : "#475569"}
                            color={theme ? "#1e293b" : "#f1f5f9"}
                            placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                            height={380}
                            fontSize={14}
                            multiline
                          />
                        )
                      ) : (
                        // Simple TextArea
                        <TextArea
                          placeholder="Document Content (use {{{fieldName}}} for template fields)"
                          value={documentData.content}
                          onChangeText={(text) => setDocumentData(prev => ({ ...prev, content: text }))}
                            backgroundColor={theme ? "#ffffff" : "#0f172a"}
                            borderColor={theme ? "#cbd5e1" : "#475569"}
                            color={theme ? "#1e293b" : "#f1f5f9"}
                            placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                            height={100}
                            numberOfLines={6}
                            fontSize={14}
                          multiline
                        />
                      )}
                    </MotiView>
                  </Card>

                  {/* <Card
                    backgroundColor={theme ? "#f0f9ff" : "#0c4a6e"}
                    borderColor={theme ? "#bfdbfe" : "#075985"}
                    borderRadius={12}
                    padding="$3"
                    marginTop="$3"
                  >
                    <XStack alignItems="flex-start" gap="$2">
                      <MaterialIcons
                        name="info"
                        size={16}
                        color={theme ? "#0284c7" : "#38bdf8"}
                        style={{ marginTop: 2 }}
                      />
                      <YStack flex={1} gap="$1">
                        <SizableText
                          size="$2"
                          color={theme ? "#0c4a6e" : "#bae6fd"}
                          lineHeight={18}
                        >
                          Use the template field button (📁) in the toolbar to insert template fields, or type {`{{{fieldName}}}`} manually. Template fields are auto-detected when you preview the document.
                        </SizableText>
                        {isEditMode && isEditorConnected && (
                          <XStack alignItems="center" gap="$1" marginTop="$1">
                            <MaterialIcons name="check-circle" size={14} color="#10b981" />
                            <SizableText size="$2" color="#10b981" fontWeight="600">
                              Connected to HelpSidePanel for AI content insertion
                            </SizableText>
                          </XStack>
                        )}
                      </YStack>
                    </XStack>
                  </Card> */}
                </View>
              </Card>

              {/* Template Fields Quick Edit */}
              {templateFields.length > 0 && (
                <Card
                  backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                  borderColor={theme ? "#dcfce7" : "#065f46"}
                  borderRadius={16}
                  padding="$4"
                  marginBottom="$4"
                  elevate
                >
                  <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
                    <XStack alignItems="center" gap="$2">
                      <View style={{
                        backgroundColor: theme ? "#d1fae5" : "#047857",
                        borderRadius: 12,
                        padding: 8
                      }}>
                        <MaterialIcons
                          name="bookmark"
                          size={18}
                          color={theme ? "#059669" : "#10b981"}
                        />
                      </View>
                      <YStack>
                        <Text style={{ fontSize: 14, fontWeight: '600', color: theme ? '#065f46' : '#d1fae5' }}>
                          Template Fields ({templateFields.length})
                        </Text>
                        {isAutoDetecting && (
                          <XStack alignItems="center" gap="$1">
                            <MaterialIcons name="autorenew" size={12} color="#10b981" />
                            <SizableText size="$1" color="#10b981" fontWeight="600">
                              Auto-detecting...
                            </SizableText>
                          </XStack>
                        )}
                      </YStack>
                    </XStack>
                    <Button
                      size="$3"
                      backgroundColor={theme ? "#059669" : "#10b981"}
                      color="white"
                      borderRadius={12}
                      onPress={() => setShowFieldsEditor(true)}
                    >
                      <XStack alignItems="center" gap="$1">
                        <MaterialIcons name="edit" size={14} color="white" />
                        <SizableText color="white" size="$2" fontWeight="600">
                          Edit All
                        </SizableText>
                      </XStack>
                    </Button>
                  </XStack>

                  {templateFields.length > 3 && (
                    <Card
                      backgroundColor={theme ? "#ecfdf5" : "#065f46"}
                      borderColor={theme ? "#a7f3d0" : "#047857"}
                      borderRadius={8}
                      padding="$2"
                      marginBottom="$3"
                    >
                      <SizableText size="$2" color={theme ? "#065f46" : "#a7f3d0"} textAlign="center">
                        Showing first 3 fields. Tap "Edit All" to see all {templateFields.length} fields.
                      </SizableText>
                    </Card>
                  )}

                  <YStack gap="$3">
                    {templateFields.slice(0, 3).map((field, index) => (
                      <XStack key={field.key} alignItems="center" gap="$2">
                        <SizableText
                          size="$3"
                          color={theme ? '#065f46' : '#d1fae5'}
                          minWidth={80}
                          fontWeight="600"
                        >
                          {field.key}:
                        </SizableText>
                        <Input
                          flex={1}
                          placeholder={`Enter ${field.key}`}
                          value={field.value}
                          onChangeText={(text) => updateFieldValue(field.key, text)}
                          backgroundColor={theme ? "#ffffff" : "#0f172a"}
                          borderColor={theme ? "#a7f3d0" : "#047857"}
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                          fontSize={13}
                        />
                      </XStack>
                    ))}
                  </YStack>
                </Card>
              )}
            </ScrollView>
          </View>

          {/* Right Panel - Preview */}
          {!leftPanelWide && (
            <View style={{ flex: 1, maxWidth: '68%', minHeight: 0, height: '100%' }}>
              <Card
                flex={1}
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$0"
                elevate
              >
                <XStack
                  justifyContent="space-between"
                  alignItems="center"
                  padding="$4"
                  borderBottomWidth={1}
                  borderBottomColor={theme ? "#e2e8f0" : "#334155"}
                >
                  <XStack alignItems="center" gap="$2">
                    <View style={{
                      backgroundColor: theme ? "#fef3c7" : "#78350f",
                      borderRadius: 12,
                      padding: 10
                    }}>
                      <MaterialIcons
                        name="preview"
                        size={20}
                        color={theme ? "#d97706" : "#fbbf24"}
                      />
                    </View>
                    <Text style={{ fontSize: 16, fontWeight: '600', color: theme ? '#1e293b' : '#f1f5f9' }}>
                      Document Preview
                    </Text>
                  </XStack>
                  <XStack gap="$2">
                    {Platform.OS === 'web' && (
                      <Button
                        size="$3"
                        backgroundColor={theme ? "#7c3aed" : "#a855f7"}
                        color="white"
                        borderRadius={12}
                        onPress={() => {
                          const printWindow = window.open('', '_blank');
                          if (printWindow) {
                            printWindow.document.write(htmlContent);
                            printWindow.document.close();
                            printWindow.print();
                          }
                        }}
                      >
                        <XStack alignItems="center" gap="$1">
                          <MaterialIcons name="print" size={14} color="white" />
                          <SizableText color="white" size="$2" fontWeight="600">
                            Print
                          </SizableText>
                        </XStack>
                      </Button>
                    )}
                  </XStack>
                </XStack>

                <View style={{ flex: 1, padding: 16 }}>
                  {Platform.OS === 'web' ? (
                    <View style={{
                      flex: 1,
                      backgroundColor: theme ? "#f8fafc" : "#0f172a",
                      borderRadius: 12,
                      overflow: 'hidden',
                      borderWidth: 1,
                      borderColor: theme ? "#e2e8f0" : "#334155"
                    }}>
                      <WebView
                        ref={webViewRef}
                        originWhitelist={['*']}
                        source={{ html: htmlContent }}
                        style={{ flex: 1, backgroundColor: 'transparent' }}
                        showsVerticalScrollIndicator={true}
                        showsHorizontalScrollIndicator={false}
                        scrollEnabled={true}
                        nestedScrollEnabled={true}
                      />
                    </View>
                  ) : (
                    <ScrollView
                        style={{
                          flex: 1,
                          backgroundColor: theme ? "#f8fafc" : "#0f172a",
                          borderRadius: 12,
                          padding: 16
                        }}
                      showsVerticalScrollIndicator={true}
                      nestedScrollEnabled={true}
                    >
                        <Text style={{ fontSize: 20, fontWeight: '700', marginBottom: 12, color: theme ? '#1e293b' : '#f1f5f9' }}>
                        {documentData.title || 'Untitled Document'}
                      </Text>
                      {documentData.description && (
                          <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontStyle: 'italic', marginBottom: 24, fontSize: 14 }}>
                          {documentData.description}
                        </Text>
                      )}
                        <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', lineHeight: 24, fontSize: 14 }}>
                        {processedContent.replace(/<[^>]*>/g, '')}
                      </Text>

                      {documentData.checkBoxes?.length ? (
                          <Card
                            backgroundColor={theme ? "#ffffff" : "#1e293b"}
                            borderColor={theme ? "#e2e8f0" : "#334155"}
                            borderRadius={12}
                            padding="$4"
                            marginTop="$4"
                          >
                            <XStack alignItems="center" gap="$2" marginBottom="$3">
                              <MaterialIcons
                                name="checklist"
                                size={20}
                                color={theme ? "#0ea5e9" : "#38bdf8"}
                              />
                              <Text style={{ fontSize: 16, fontWeight: '600', color: theme ? '#1e293b' : '#f1f5f9' }}>
                                Required Actions
                              </Text>
                            </XStack>
                            <YStack gap="$2">
                              {documentData.checkBoxes.map((checkbox, index) => (
                                <View key={index} style={{
                                  padding: 12,
                                  backgroundColor: theme ? "#f8fafc" : "#334155",
                                  borderRadius: 8
                                }}>
                                  <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14 }}>
                                    □ {checkbox.label}
                                  </Text>
                                  {checkbox.value && (
                                    <Text style={{
                                      marginLeft: 20,
                                      marginTop: 4,
                                      color: theme ? '#64748b' : '#94a3b8',
                                      fontSize: 12
                                    }}>
                                      {checkbox.value}
                                    </Text>
                                  )}
                                </View>
                              ))}
                            </YStack>
                          </Card>
                        ) : null}
                      </ScrollView>
                  )}
                </View>
              </Card>
            </View>
          )}
        </XStack>

        {/* Template Fields Editor Modal */}
        <Modal
          animationType="slide"
          transparent={true}
          visible={showFieldsEditor}
          onRequestClose={() => setShowFieldsEditor(false)}
        >
          <View style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.6)'
          }}>
            <Card
              width="90%"
              maxHeight="80%"
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={20}
              padding="$5"
              elevate
            >
              <XStack justifyContent="space-between" alignItems="center" marginBottom="$4">
                <XStack alignItems="center" gap="$2">
                  <View style={{
                    backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
                    borderRadius: 12,
                    padding: 10
                  }}>
                    <MaterialIcons
                      name="view-list"
                      size={22}
                      color={theme ? "#0ea5e9" : "#38bdf8"}
                    />
                  </View>
                  <Text style={{ fontSize: 18, fontWeight: '700', color: theme ? '#1e293b' : '#f1f5f9' }}>
                    Template Fields
                  </Text>
                </XStack>
                <Button
                  size="$3"
                  backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                  color="white"
                  borderRadius={12}
                  onPress={() => setShowFieldsEditor(false)}
                >
                  <XStack alignItems="center" gap="$1">
                    <MaterialIcons name="check" size={16} color="white" />
                    <SizableText color="white" size="$3" fontWeight="600">
                      Done
                    </SizableText>
                  </XStack>
                </Button>
              </XStack>

              {/* Add Checkbox Section */}
              <Card
                backgroundColor={theme ? "#f8fafc" : "#334155"}
                borderColor={theme ? "#e2e8f0" : "#475569"}
                borderRadius={12}
                padding="$4"
                marginBottom="$4"
              >
                <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons
                      name="checklist"
                      size={18}
                      color={theme ? "#0ea5e9" : "#38bdf8"}
                    />
                    <Text style={{ fontSize: 14, fontWeight: '600', color: theme ? '#1e293b' : '#f1f5f9' }}>
                      Document Actions
                    </Text>
                  </XStack>
                </XStack>
                <Button
                  size="$3"
                  backgroundColor={theme ? "#059669" : "#10b981"}
                  color="white"
                  borderRadius={12}
                  onPress={addCheckbox}
                >
                  <XStack alignItems="center" gap="$1">
                    <AntDesign name="plus" size={14} color="white" />
                    <SizableText color="white" size="$3" fontWeight="600">
                      Add Checkbox
                    </SizableText>
                  </XStack>
                </Button>

                <YStack gap="$3" marginTop="$3">
                  {documentData.checkBoxes?.map((checkbox, index) => (
                    <Card
                      key={index}
                      backgroundColor={theme ? "#ffffff" : "#1e293b"}
                      borderColor={theme ? "#cbd5e1" : "#475569"}
                      borderRadius={10}
                      padding="$3"
                    >
                      <XStack gap="$2" alignItems="center" marginBottom="$2">
                        <Input
                          flex={1}
                          placeholder="Checkbox label"
                          value={checkbox.label}
                          onChangeText={(text) => updateCheckbox(index, 'label', text)}
                          backgroundColor={theme ? "#f8fafc" : "#0f172a"}
                          borderColor={theme ? "#cbd5e1" : "#475569"}
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                          fontSize={13}
                        />
                        <Button
                          size="$2"
                          backgroundColor="#ef4444"
                          color="white"
                          borderRadius={8}
                          onPress={() => removeCheckbox(index)}
                          padding="$2"
                        >
                          <AntDesign name="delete" size={14} color="white" />
                        </Button>
                      </XStack>
                      <XStack gap="$2" alignItems="center">
                        <Switch
                          checked={checkbox.required}
                          onCheckedChange={(checked) => updateCheckbox(index, 'required', checked)}
                        />
                        <SizableText size="$3" color={theme ? '#64748b' : '#94a3b8'} fontWeight="500">
                          Required
                        </SizableText>
                      </XStack>
                    </Card>
                  ))}
                </YStack>
              </Card>

              <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginVertical="$2" />

              <ScrollView style={{ maxHeight: 400 }}>
                {templateFields.length === 0 ? (
                  <Card
                    backgroundColor={theme ? "#f0f9ff" : "#0c4a6e"}
                    borderColor={theme ? "#bfdbfe" : "#075985"}
                    borderRadius={12}
                    padding="$5"
                  >
                    <YStack alignItems="center" gap="$3">
                      <MaterialIcons
                        name="info-outline"
                        size={40}
                        color={theme ? "#0284c7" : "#38bdf8"}
                      />
                      <SizableText
                        color={theme ? '#0c4a6e' : '#bae6fd'}
                        textAlign="center"
                        size="$4"
                      >
                        No template fields found. Add template fields using {`{{{fieldName}}}`} syntax in your document content.
                      </SizableText>
                    </YStack>
                  </Card>
                ) : (
                    <YStack gap="$3">
                    {templateFields.map((field, index) => (
                      <Card
                        key={field.key}
                        backgroundColor={theme ? "#f8fafc" : "#334155"}
                        borderColor={theme ? "#cbd5e1" : "#475569"}
                        borderRadius={12}
                        padding="$4"
                      >
                        <XStack alignItems="center" gap="$2" marginBottom="$2">
                          <MaterialIcons
                            name="label"
                            size={16}
                            color={theme ? "#0ea5e9" : "#38bdf8"}
                          />
                          <Text style={{
                            fontSize: 14,
                            fontWeight: '600',
                            color: theme ? '#1e293b' : '#f1f5f9'
                          }}>
                            {field.key}
                          </Text>
                        </XStack>
                        <Input
                          placeholder={`Enter value for ${field.key}`}
                          value={field.value}
                          onChangeText={(text) => updateFieldValue(field.key, text)}
                          backgroundColor={theme ? "#ffffff" : "#1e293b"}
                          borderColor={theme ? "#cbd5e1" : "#475569"}
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                          fontSize={14}
                        />
                      </Card>
                    ))}
                    </YStack>
                )}
              </ScrollView>
            </Card>
          </View>
        </Modal>
      </DefaultScreen>
    </ErrorBoundary>
  );
};

export default DocumentTemplateEditor;
